/** @fileoverview Tells app renderer components whether they run in the builder preview or on a hosted app page, and how they read data and checklist or timeline rows. */

import { createContext, useContext, useMemo } from 'react';

import type { ComponentStateApi } from '../../services/componentStateApi';
import type {
  AppActionWrite,
  FabrikGlobals,
  FabrikListQueryOptions,
  FabrikSystemDataSource,
  FabrikVariable,
} from '../../types/apps';
import type { FieldOptionsReader, FieldValuesReader, LabelOf } from './fieldOptions';
import { createFabrikGlobals } from './globals';
import type { ValueContext } from './helpers';

export type AppRuntimeMode = 'builder' | 'hosted';

export type DataReadOptions = FabrikListQueryOptions;

export type DataReader = (request: {
  dataSource: FabrikSystemDataSource;
  options: DataReadOptions;
  signal?: AbortSignal;
}) => Promise<unknown>;

export type AppRuntimeValue = {
  mode: AppRuntimeMode;
  readData: DataReader;
  /** Reads the values system fields can take, with labels; cached per page. */
  readFieldOptions: FieldOptionsReader;
  /** Searches the values of one system field page by page, or finds the labels of some codes; cached per page. */
  readFieldValues: FieldValuesReader;
  /** Finds the label of a value read from a data source field (picklists, foundation objects). */
  labelOf: LabelOf;
  /** Puts the values saved writes sent into the open screens that show those records. */
  applyWrites: (writes: AppActionWrite[]) => void;
  globals: FabrikGlobals;
  /** Null in the builder when the app has no id yet, so rows cannot be read or written. */
  componentState: ComponentStateApi | null;
  /** The app's variables, read from the definition. Empty when the definition declares none. */
  variables: FabrikVariable[];
  /** Goes up by one each time the screen's data must be read again, for example after a write. */
  dataVersion: number;
  /** Tells every list on the screen to read its data again. */
  reloadData: () => void;
};

const AppRuntimeContext = createContext<AppRuntimeValue>({
  mode: 'builder',
  readData: () => Promise.reject(new Error('No data reader is available.')),
  readFieldOptions: () => Promise.resolve({}),
  readFieldValues: () => Promise.resolve({ results: [], hasMore: false }),
  labelOf: () => undefined,
  applyWrites: () => undefined,
  globals: createFabrikGlobals(),
  componentState: null,
  variables: [],
  dataVersion: 0,
  reloadData: () => undefined,
});

export const AppRuntimeProvider = AppRuntimeContext.Provider;

/** Returns true when the renderer runs on a hosted app page, where builder calls are not allowed. */
export const useIsHosted = () => useContext(AppRuntimeContext).mode === 'hosted';

/** Returns the current render's globals snapshot: the clock and the signed-in viewer. */
export const useFabrikGlobals = () => useContext(AppRuntimeContext).globals;

/** Returns the function that reads list data for the current mode. */
export const useDataReader = () => useContext(AppRuntimeContext).readData;

/** Returns the function that puts saved write values into the open screens. */
export const useApplyWrites = () => useContext(AppRuntimeContext).applyWrites;

/** Returns the function that finds the label of a value read from a data source field. */
export const useLabelOf = () => useContext(AppRuntimeContext).labelOf;

/** Returns the function that reads the values system fields can take, with labels. */
export const useFieldOptionsReader = () => useContext(AppRuntimeContext).readFieldOptions;

/** Returns the function that searches one system field's values or finds the labels of some codes. */
export const useFieldValuesReader = () => useContext(AppRuntimeContext).readFieldValues;

/** Returns the number that changes each time the screen's data must be read again. */
export const useDataVersion = () => useContext(AppRuntimeContext).dataVersion;

/** Returns the function that tells every list on the screen to read its data again. */
export const useReloadData = () => useContext(AppRuntimeContext).reloadData;

/** Returns the checklist and timeline row functions, or null when rows are not available. */
export const useComponentState = () => useContext(AppRuntimeContext).componentState;

/** Returns the app's variables and the globals together, as the value helpers read them. */
export const useValueContext = (): ValueContext => {
  const { variables, globals, labelOf } = useContext(AppRuntimeContext);
  return useMemo(() => ({ variables, globals, labelOf }), [variables, globals, labelOf]);
};
